import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, CheckCircle2, XCircle } from 'lucide-react';
import { vendorApi } from '../../services/api';

interface ScanResult {
  success: boolean;
  message: string;
  details?: Record<string, string>;
}

interface QrScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CONTAINER_ID = 'qr-scanner-video-container';

const QrScannerModal: React.FC<QrScannerModalProps> = ({ isOpen, onClose }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  // Use refs to avoid stale closures in the html5-qrcode onScan callback
  const isProcessingRef = useRef(false);
  const resultRef = useRef<ScanResult | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        if (scannerRef.current.isScanning) {
          await scannerRef.current.stop();
        }
        scannerRef.current.clear();
      } catch (_) {
        // Ignore stop errors — scanner may already be stopped
      }
      scannerRef.current = null;
    }
  }, []);

  const handleScan = useCallback(async (rawData: string) => {
    if (isProcessingRef.current || resultRef.current) return;

    isProcessingRef.current = true;
    setIsProcessing(true);

    let scanResult: ScanResult;

    try {
      const data = JSON.parse(rawData) as Record<string, unknown>;
      const type = data['type'] as string | undefined;
      const id = data['id'] as number | undefined;
      const instanceId = data['instance_id'] as number | undefined;
      const qrRef = data['ref'] as string | undefined;

      if (!type || !id) {
        scanResult = {
          success: false,
          message: 'Invalid QR code format. This is not a valid booking QR code.',
        };
      } else {
        const responseData = await vendorApi.scanCheckIn({ type, id, instance_id: instanceId });

        const details: Record<string, string> = {};
        if (qrRef) details['Reference'] = qrRef;
        if (responseData?.venue_name) details['Venue'] = responseData.venue_name;
        if (responseData?.court_name) details['Court'] = responseData.court_name;
        if (responseData?.booking_date) details['Date'] = responseData.booking_date;
        if (responseData?.start_time) {
          details['Time'] = `${responseData.start_time}${responseData.end_time ? ` - ${responseData.end_time}` : ''}`;
        }
        if (responseData?.customer_name) details['Customer'] = responseData.customer_name;

        scanResult = {
          success: true,
          message: 'Customer checked in successfully',
          details: Object.keys(details).length > 0 ? details : undefined,
        };
      }
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        scanResult = {
          success: false,
          message: 'Invalid QR code. Please scan a booking QR code from the Spotts app.',
        };
      } else {
        const message =
          err?.response?.data?.message || err?.message || 'Check-in failed. Please try again.';
        scanResult = { success: false, message };
      }
    }

    resultRef.current = scanResult;
    isProcessingRef.current = false;
    setIsProcessing(false);
    setResult(scanResult);
  }, []);

  const startScanner = useCallback(async () => {
    setCameraError(null);

    try {
      const scanner = new Html5Qrcode(CONTAINER_ID);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          // Define qrbox so html5-qrcode restricts scanning to the center region
          qrbox: { width: 220, height: 220 },
          // Don't flip — use environment camera as-is
          disableFlip: false,
        },
        handleScan,
        // Suppress per-frame "not found" errors (they fire constantly while scanning)
        () => {}
      );
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.toLowerCase().includes('permission') || msg.toLowerCase().includes('notallowed')) {
        setCameraError('Camera permission denied. Please allow camera access and try again.');
      } else if (msg.toLowerCase().includes('notfound') || msg.toLowerCase().includes('no camera')) {
        setCameraError('No camera found. Please use a device with a camera.');
      } else {
        setCameraError('Could not start camera. Please check your camera and try again.');
      }
      scannerRef.current = null;
    }
  }, [handleScan]);

  useEffect(() => {
    if (isOpen) {
      // Brief delay to ensure the DOM node is rendered before starting scanner
      const timer = setTimeout(startScanner, 150);
      return () => clearTimeout(timer);
    } else {
      stopScanner();
      setResult(null);
      setIsProcessing(false);
      setCameraError(null);
      isProcessingRef.current = false;
      resultRef.current = null;
    }
  }, [isOpen, startScanner, stopScanner]);

  const handleClose = useCallback(async () => {
    await stopScanner();
    onClose();
  }, [stopScanner, onClose]);

  const resetScanner = useCallback(() => {
    resultRef.current = null;
    isProcessingRef.current = false;
    setResult(null);
    setIsProcessing(false);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
    >
      <div className="relative w-full max-w-sm">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 px-1">
          <h2 className="text-white text-lg font-semibold tracking-wide">Scan QR Code</h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-full text-white hover:bg-white/10 transition-colors"
            aria-label="Close scanner"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Camera viewport */}
        <div className="relative w-full rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: '1 / 1' }}>
          {/* html5-qrcode renders video into this div */}
          <div
            id={CONTAINER_ID}
            className="absolute inset-0"
            style={{
              // Override html5-qrcode's default styles so video fills the box
              // The library injects inline styles; these ensure the video is full-size
              width: '100%',
              height: '100%',
            }}
          />

          {/* Custom overlay — 4 dark strips leaving a center square clear */}
          {!cameraError && (
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
              {/* Top strip */}
              <div className="absolute top-0 left-0 right-0" style={{ height: 'calc(50% - 110px)', backgroundColor: 'rgba(0,0,0,0.55)' }} />
              {/* Bottom strip */}
              <div className="absolute bottom-0 left-0 right-0" style={{ height: 'calc(50% - 110px)', backgroundColor: 'rgba(0,0,0,0.55)' }} />
              {/* Left strip */}
              <div
                className="absolute left-0"
                style={{
                  top: 'calc(50% - 110px)',
                  height: '220px',
                  width: 'calc(50% - 110px)',
                  backgroundColor: 'rgba(0,0,0,0.55)',
                }}
              />
              {/* Right strip */}
              <div
                className="absolute right-0"
                style={{
                  top: 'calc(50% - 110px)',
                  height: '220px',
                  width: 'calc(50% - 110px)',
                  backgroundColor: 'rgba(0,0,0,0.55)',
                }}
              />

              {/* Corner brackets — positioned at edges of the 220×220 clear zone */}
              <div
                className="absolute"
                style={{ top: 'calc(50% - 110px)', left: 'calc(50% - 110px)', width: 220, height: 220 }}
              >
                {/* Top-left */}
                <div className="absolute top-0 left-0 w-8 h-8 border-t-[3.5px] border-l-[3.5px] border-primary-500 rounded-tl-lg" />
                {/* Top-right */}
                <div className="absolute top-0 right-0 w-8 h-8 border-t-[3.5px] border-r-[3.5px] border-primary-500 rounded-tr-lg" />
                {/* Bottom-left */}
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-[3.5px] border-l-[3.5px] border-primary-500 rounded-bl-lg" />
                {/* Bottom-right */}
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-[3.5px] border-r-[3.5px] border-primary-500 rounded-br-lg" />
              </div>
            </div>
          )}

          {/* Processing spinner */}
          {isProcessing && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60">
              <div className="p-5 rounded-2xl bg-black/80">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            </div>
          )}

          {/* Camera error state */}
          {cameraError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-center p-6">
              <XCircle className="h-10 w-10 text-red-400 mb-3" />
              <p className="text-white text-sm leading-relaxed">{cameraError}</p>
            </div>
          )}
        </div>

        {/* Instruction text */}
        {!result && !isProcessing && !cameraError && (
          <p className="text-center text-white/70 text-sm mt-4">
            Point the camera at the customer's booking QR code
          </p>
        )}

        {/* Result panel */}
        {result && (
          <div className="mt-4 bg-white rounded-2xl p-5">
            <div className="flex flex-col items-center">
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center ${
                  result.success ? 'bg-green-100' : 'bg-red-100'
                }`}
              >
                {result.success ? (
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                ) : (
                  <XCircle className="h-8 w-8 text-red-600" />
                )}
              </div>

              <h3
                className={`mt-3 text-lg font-bold ${
                  result.success ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {result.success ? 'Check-in Successful' : 'Check-in Failed'}
              </h3>

              <p className="text-center text-sm text-gray-500 mt-1 leading-relaxed">
                {result.message}
              </p>

              {result.details && (
                <div className="w-full mt-4 bg-gray-50 rounded-xl p-3 space-y-2">
                  {Object.entries(result.details).map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between gap-4">
                      <span className="text-xs text-gray-500 flex-shrink-0">{label}</span>
                      <span className="text-xs font-semibold text-gray-800 text-right">{value}</span>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={resetScanner}
                className="mt-5 w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors text-sm"
              >
                Scan Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Suppress html5-qrcode default UI chrome via global styles in this modal's scope */}
      <style>{`
        #${CONTAINER_ID} video {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
        }
        #${CONTAINER_ID} canvas {
          display: none !important;
        }
        #${CONTAINER_ID}__scan_region {
          border: none !important;
          box-shadow: none !important;
          background: transparent !important;
        }
        #${CONTAINER_ID}__scan_region img {
          display: none !important;
        }
        #${CONTAINER_ID}__dashboard {
          display: none !important;
        }
      `}</style>
    </div>
  );
};

export default QrScannerModal;
